/**
 * @puffin-core/auth-client — shared internal helpers.
 *
 * @module @puffin-core/auth-client/internal
 */

/**
 * Canonical "no token" message — matches the legacy pos-web `buildAuthHeaders()`
 * throw so consumer copy is unchanged.
 */
import {
  SESSION_UNREACHABLE_REASONS,
  SessionUnreachableError,
} from '@puffin-core/api-client/session-unreachable';

export const AUTH_REQUIRED_MESSAGE = 'Authentication required. Please log in and try again.';

/**
 * The machine code every TRANSPORT failure in this package carries (Q-QF2-3c).
 *
 * ⚠️ Why this exists. Before it, a transport failure threw a bare
 * `new Error('Network error…')` with no `code` and no `response`, so consumers
 * had to classify it by ABSENCE: "no code AND no response ⇒ the network is
 * down". pos-web's `usePINLogin.resolveErrorLeaf` did exactly that, and the
 * inference was sound only because a transport throw was the ONLY uncoded,
 * response-less rejection those paths could produce. That is not a property
 * anyone can maintain: `onSuccess()`, `authStorage.setAuth()` and
 * `handleAuthSuccess()` all run inside the caller's `try`, so a throwing
 * post-success callback — a plain `TypeError` — was rendered to the user as
 * "Unable to connect to server."
 *
 * A code makes transport failure a POSITIVE fact instead of a residual one, so
 * "we could not classify this" and "we never reached the server" stop
 * collapsing into each other. Also SCREAMING_SNAKE, matching RES-ENV-001's
 * server vocabulary, so one client table classifies both origins.
 *
 * @constant {string}
 */
export const NETWORK_ERROR_CODE = 'NETWORK_ERROR';

/**
 * Build the canonical TRANSPORT-failure error (`res.networkError === true`).
 *
 * The message is unchanged from the bare `new Error(message)` it replaces —
 * consumer copy and every existing message assertion still hold — but the
 * error now carries `code: 'NETWORK_ERROR'`. Nothing here sets `response`: by
 * definition there was no response, and inventing one would defeat the
 * distinction this function exists to draw.
 *
 * @param {string} message - user-facing fallback copy for consumers with no catalog
 * @returns {Error} an Error carrying `code = NETWORK_ERROR_CODE`
 */
export function networkError(message) {
  const err = new Error(message);
  err.code = NETWORK_ERROR_CODE;
  // No `status` on purpose — there was no HTTP answer, and `web/no-status-stripped-auth-error`
  // forbids a hardcoded status on a thrown auth error (a literal here would mislabel the
  // failure downstream). The network fact travels as the POSITIVE `code` above; the fleet's
  // server-side fields are explicitly empty so readers see one shape.
  err.errorCode = null;
  err.errorDetails = null;
  return err;
}

/**
 * Reject a session check the server could not ANSWER (POS-OFFLINE-STATION-LOCK-001 U15b, owner ruling Q6): no answer
 * at all, a 5xx, or a 408 — the server (or a gateway in front of it) judged nothing, so the session is not over. Every
 * other answer is the server's, and the caller reads it.
 *
 * @param {{ok: boolean, status: number, networkError?: boolean}} res - The transport's result.
 * @param {string} endpoint - The check's path.
 * @returns {void}
 * @throws {SessionUnreachableError}
 */
export function throwIfUnreachable(res, endpoint) {
  if (res.ok === true) return;
  if (res.networkError === true) {
    throw new SessionUnreachableError({ endpoint, reason: SESSION_UNREACHABLE_REASONS.NO_ANSWER });
  }
  if (res.status >= 500 || res.status === 408) {
    throw new SessionUnreachableError({
      endpoint,
      reason: SESSION_UNREACHABLE_REASONS.SERVER_FAILURE,
      status: res.status,
    });
  }
}

/**
 * Extract the inner payload from a backend wire body.
 *
 * 🔴 RES-CLIENT-001 (2026-08-26) — PROVE-THEN-NARROW (owner ruling). This was
 * `body?.data ?? body`, and its docblock advertised a DUAL contract: *"the transport delivers
 * `body` in on-the-wire shape (`{ success, data }` for enveloped routes; the raw object for
 * un-enveloped routes)"*. The ruling was to audit every route this client calls, delete the dual
 * contract if all are enveloped, and fix the ROUTE — not the client — if any were not.
 *
 * **The audit, measured rather than assumed:**
 *
 * - This client requests ~40 paths, all under the `/api/v1/auth` base, and every one is served by
 *   a `@puffin-core/auth-routes` factory. Across that entire package: **119 `sendSuccess`, ZERO
 *   bare `res.json`.**
 * - The only other routers mounted at `/v1/auth` are the per-app `marketingLoginAuthRoutes` thin
 *   wirers, and they serve two paths this client never calls (`/subdomain-lookup`,
 *   `/access-request`) — themselves assembled from the same shared factories.
 *
 * So there is no un-enveloped route, and the `?? body` arm was unreachable. When it *did* fire it
 * returned the whole wire body as the payload, which for an auth client means handing the caller
 * an object with no `accessToken` and no `user` and letting it decide what that meant.
 *
 * The original `??`-not-`||` reasoning still holds and is why this is `body?.data` rather than a
 * truthiness test: a legitimately `null` payload stays `null` instead of being replaced.
 *
 * NARROWED rather than routed through `readEnvelopeData`: this package does not declare
 * `@puffin-core/api-client`, and adding that dependency would be a shared-tree mutation.
 *
 * @param {*} body - the RES-ENV-001 wire body
 * @returns {*} the envelope's payload
 */
export function extractData(body) {
  return body?.data;
}

/**
 * Read the server-provided error message from a backend error body, falling
 * back to a supplied default. Reads the ONE wire shape — the RES-ENV-001 error
 * envelope `{ success: false, error: { code, message, details? } }`. The legacy
 * bare-string `error` and top-level `message` arms were RETIRED by RES-ENV-001 and
 * are gone here too (AUTH-ERROR-VOCABULARY-001): a body in either shape is not a
 * Colony API answer, and reading it as one hid exactly that.
 *
 * @param {*} body
 * @param {string} fallback
 * @returns {string}
 */
export function errorMessage(body, fallback) {
  return body?.error?.message || fallback;
}

/**
 * Build the canonical auth-client error for a non-2xx transport result.
 *
 * AUTH-LOGIN-FIDELITY-001: every HTTP-error throw in this package MUST carry the
 * real HTTP status so downstream classifiers (`@puffin-core/login-shell`
 * `translateAuthError`, which reads `err.status`) can distinguish credentials
 * (401/403/404) from rate-limit (429), server (5xx), and bad-request (400).
 * Without this, a status-stripped `new Error(...)` is misclassified as a
 * transport/network failure ("Network error.").
 *
 * AUTH-ERROR-VOCABULARY-001 (2026-08-28): the error speaks the FLEET vocabulary —
 * `status · message · errorCode · errorDetails` (+ `data`, the wire body) — the same
 * four fields `@puffin-core/api-client`'s normalized error and pos-web's processed
 * error carry, so every reader in the web tier has ONE spelling. Nothing here sets
 * `.response`: the axios-shaped `{ status, data }` this used to re-mint was a second
 * contract that kept 24 web reads on a shape three of the four producers never emit.
 *
 * @param {{status: number, body: *}} res  Transport result (`{ ok, status, body }`).
 * @param {string} fallback                Default user-facing message.
 * @returns {Error} `Error{ status, message, errorCode, errorDetails, data }`
 */
export function authError(res, fallback) {
  const body = res?.body ?? null;
  const err = new Error(errorMessage(body, fallback));
  err.status = res?.status;
  err.errorCode = typeof body?.error?.code === 'string' ? body.error.code : null;
  err.errorDetails = body?.error?.details ?? null;
  err.data = body;
  return err;
}

/**
 * Build the `mustHaveToken()` gate. Throws the canonical `AUTH_REQUIRED_MESSAGE`
 * when no access token is available (preserves the legacy `buildAuthHeaders()`
 * contract: throw-style methods propagate it; result-object methods catch it).
 *
 * @param {{getAccessToken: () => (string|null)}} tokenStore
 * @returns {() => string}
 */
export function createTokenGate(tokenStore) {
  return function mustHaveToken() {
    const token = tokenStore?.getAccessToken?.();
    if (!token) {
      throw new Error(AUTH_REQUIRED_MESSAGE);
    }
    return token;
  };
}
